import React, { useMemo } from "react";
import Switch from "react-switch";
import getYear from "date-fns/getYear";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { File } from "react-feather";
import classNames from "classnames";

import TitleMeta from "../../common/components/TitleMeta";
import NumberPager from "../../common/components/NumberPager";
import Download from "../../common/components/DownloadCsv";
import { primary, secondary } from "../../common/helpers/colors";

import { useTypedSelector } from "../../store/models/root.interface";
import { setIsCalendar } from "../../store/actions/marketing.actions";
import { FileData, MarketingActivity as Activity } from "../../api";

import CsvColumns from "./models/activity-csv.columns";

import List from "./MarketingActivitiesList";
import Calendar from "./MarketingActivitiesCalendar";
import Layout from "../../components/Layout/Layout";

export interface MarketingActivitiesProps {
  activities?: Activity[];
  file?: FileData;
  year: number;
  isLoading: boolean;
  handleSelect: (id: string) => any;
}

const MarketingActivities: React.FC<MarketingActivitiesProps> = ({
  activities,
  file,
  year,
  isLoading,
  handleSelect,
}) => {

  const thisYear = getYear(new Date());
  const minYear = thisYear - 1;
  const maxYear = thisYear + 1;

  const dispatch = useDispatch();
  const history = useHistory();

  const csv = useMemo(() => !!activities
    ? activities
    : [],
    [activities]);

  const isCalendar = useTypedSelector(
    state => state.marketing.isCalendar);

  const setYear = (year: number) => {
    history.replace(year !== thisYear
      ? `/marketing/activities/${year}`
      : "/marketing/activities");
  }

  const onViewChange = (swtch: boolean) => {
    dispatch(setIsCalendar(swtch));
  };

  return (
    <>
      <TitleMeta title="Marketing Activities" />

      <Layout.Header>

        <Layout.Breadcrumb items={["Marketing"]} />

        <div className="d-flex align-items-start justify-content-between">
          <div className="d-flex align-items-center">
            {/* Title */}
            <Layout.Title>Activities</Layout.Title>
            {/* Year paging */}
            <div className="ml-3">
              <NumberPager value={year} min={minYear} max={maxYear} onChange={setYear} />
            </div>
          </div>
          <div className="d-flex align-items-center">
            {/* View mode toggle */}
            <div>
              <label className="d-flex align-items-center mb-0">
                <span className="text-uppercase">List</span>
                <div className="mx-2 d-flex">
                  <Switch
                    height={24}
                    width={48}
                    handleDiameter={20}
                    checked={isCalendar}
                    onChange={onViewChange}
                    onColor={primary}
                    offColor={primary}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    activeBoxShadow={`0 0 2px 3px ${secondary}`} />
                </div>
                <span className="text-uppercase">Calendar</span>
              </label>
            </div>
            {/* Download */}
            <div className="ml-3">
              <a href={file?.url ?? "#0"}
                className={classNames({
                  "btn btn-sm btn-neutral d-flex align-items-center": true,
                  "disabled": isLoading || !file,
                })}>
                <File size={14} />
                <span className="ml-1">Schedule</span>
              </a>
            </div>
            {/* Export */}
            {csv.length !== 0 && (
              <div className="ml-3">
                <Download
                  filename={`Marketing Schedule ${year}`}
                  columns={CsvColumns}
                  data={csv}
                  disabled={isLoading}
                />
              </div>)}
          </div>
        </div>
      </Layout.Header>

      <Layout.Body>
        {isCalendar
          ? (
            <Calendar
              activities={activities}
              year={year}
              isLoading={isLoading}
              handleSelect={handleSelect} />)
          : (
            <List
              activities={activities}
              isLoading={isLoading}
              handleSelect={handleSelect} />)}
      </Layout.Body>
    </>
  );
};

export default MarketingActivities;
